import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button, IconButton, Loading, Spinner, Typography, Modal } from "@/components/atoms";
import { useProfile, usePrompt } from "@/hooks";
import { useGetProfileQuery } from "@/redux/apis/profile/profileApi";
import { getAssetUrl, getTimezoneFlag } from "@/utils";
import { ProfileAvatarUploader } from "./ProfileAvatarUploader";
import { ProfileFormData } from "./types";

export const ProfileHeaderEdit = () => {
  const navigate = useNavigate();
  const profile = useProfile();
  const { refetch, isFetching } = useGetProfileQuery();
  const [showModal, setShowModal] = useState(false);

  const {
    formState: { isDirty, isSubmitted, isSubmitting },
  } = useFormContext<ProfileFormData>();

  usePrompt({
    when: isDirty && !isSubmitted && !isSubmitting,
    message: "Changes will not be saved. Do you want to proceed?",
  });

  if (!profile) return <Spinner />;

  const { firstName, lastName, title, timezone, avatar } = profile;
  const avatarUrl =
    getAssetUrl(avatar, { height: 328, width: 328, fit: "cover" }) ||
    `https://via.placeholder.com/328x328/cccccc/969696?text=${firstName[0]}`;

  return (
    <div className="relative">
      {isFetching && <Loading />}
      <div className="relative h-[128px] bg-gradient-to-r from-[#A18CD1] to-[#FBC2EB] lg:h-[164px]"></div>
      <div className="-mt-12 flex items-center gap-8 bg-white">
        <div className="relative ml-8 lg:ml-12">
          <img src={avatarUrl} className="h-[128px] w-[128px] rounded-full border-[11px] border-white bg-white lg:h-[164px] lg:w-[164px]" />
          <IconButton
            onClick={() => setShowModal(true)}
            iconName="Camera"
            iconVariant="Bold"
            iconSize="md"
            className="absolute bottom-3 right-3 !rounded-full !bg-info !p-2 hover:opacity-80"
          />
        </div>
        <div className="mt-12 flex-1">
          <Typography variant="h2" className="leading-loose">
            {title} {firstName} {lastName}
          </Typography>
          <Typography className="text-neutral-dark-gray">
            {getTimezoneFlag(timezone.name)} <span className="ml-1">{timezone.name}</span>
          </Typography>
        </div>
        <div className="mr-6 mt-12 flex gap-3">
          <Button variant="secondary" onClick={() => navigate(-1)} size="md" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" size="md" form="editProfile" type="submit" disabled={!isDirty || isSubmitting}>
            {isSubmitting ? <Spinner /> : "Save"}
          </Button>
        </div>
      </div>
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Upload Profile Photo">
        <ProfileAvatarUploader
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            refetch();
          }}
        />
      </Modal>
    </div>
  );
};
